// Minimal dependency-free focus trap for modal dialogs (WCAG 2.4.3).
// Usage:
//   const deactivate = activateFocusTrap(containerEl, onEscape);
//   ...later: const previouslyFocused = deactivate();
//
// - Stores document.activeElement on activation.
// - Moves focus to the first focusable element inside the container
//   (or the container itself if it has tabindex="-1" and no focusable children).
// - Tab / Shift+Tab wrap around at the container's first/last focusable element.
// - Escape calls onEscape (the caller is responsible for hiding the dialog).
// - The returned deactivate function removes all listeners and returns the
//   previously focused element so the caller can restore focus if needed.

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function activateFocusTrap(containerEl, onEscape) {
  if (!containerEl) {
    return function noop() {
      return null;
    };
  }

  const previouslyFocused = document.activeElement;

  const getFocusable = () =>
    Array.from(containerEl.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement
    );

  // Move initial focus into the dialog.
  const focusable = getFocusable();
  if (focusable.length > 0) {
    focusable[0].focus();
  } else if (containerEl.getAttribute("tabindex") === "-1") {
    containerEl.focus();
  }

  const onKeydown = (event) => {
    if (event.key === "Escape") {
      if (typeof onEscape === "function") {
        onEscape(event);
      }
      return;
    }

    if (event.key !== "Tab") {
      return;
    }

    const items = getFocusable();
    if (items.length === 0) {
      // Nothing focusable inside: keep focus on the container.
      event.preventDefault();
      if (containerEl.getAttribute("tabindex") === "-1") {
        containerEl.focus();
      }
      return;
    }

    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;

    if (event.shiftKey) {
      // Shift+Tab on (or before) the first item wraps to the last.
      if (active === first || !containerEl.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else {
      // Tab on (or after) the last item wraps to the first.
      if (active === last || !containerEl.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  document.addEventListener("keydown", onKeydown, true);

  // Deactivate: remove listeners, hand back the previously focused element.
  return function deactivate() {
    document.removeEventListener("keydown", onKeydown, true);
    return previouslyFocused;
  };
}
